<template>
    <div v-for="(placement, placementIndex) in visiblePlacements"
        :key="placement.code"
        class="px-[20px] lg:px-[40px]"
    >
        <template v-if="!!shaftPlacementMapped && placement.code === 'Shaft'">
            <div class="mt-[29.73px]"></div>

            <placement-header :placement="placement" :index="placementIndex" />

            <base-input-select
                class="mb-[24px]"
                :select-options="mfgOptionsForDropdowns"
                @input="handleMfgSelect"
                v-model="selectedMfgOption"
            />

            <base-input-select-with-image
                class="mb-[16px]"
                :select-options="modelOptionsForDropdowns"
                @input="handleModelSelect"
                v-model="selectedModelOption"
            />

            <div
                class="flex items-center justify-center p-2 border border-gray-light mb-[16px]"
                v-if="previewImage"
            >
                <img :src="previewImage" alt="Grip Image" />
            </div>
        </template>
    </div>
    <div class="h-[2px]"></div>
</template>

<script>
    import allStores from '../stores/all';

    import BaseInputSelect from './BaseInputSelect.vue';
    import PlacementHeader from './PlacementHeader.vue';
    import getImage from '../helpers/getimage';
    import BaseInputSelectWithImage from './BaseInputSelectWithImage.vue';

    export default {
        mixins: [allStores],

        components: {
            BaseInputSelect,
            BaseInputSelectWithImage,
            PlacementHeader,
        },

        emits: ['applySelections'],

        props: {
            itemData: {
                type: Object,
                required: true,
            },
            isDirty: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                previewImage: null,

                selectedMfgOption: null,
                selectedModelOption: null,
            };
        },

        computed: {
            panelToReset() {
                return this.customizerStore.panelToReset;
            },

            currentGroupName() {
                return this.customizerStore.currentGroup;
            },

            mainPlacement() {
                return this.visiblePlacements.find((x) => x.code === 'Shaft');
            },

            selectedPlacement() {
                return this.viewController.selectedAtPlacement(this.mainPlacement.code);
            },

            shaftPlacement() {
                return this.visiblePlacements.find((placement) => placement.code === 'Shaft');
            },

            shaftPlacementMapped() {
                if (!this.shaftPlacement) {
                    return null;
                }

                const list = this.shaftPlacement.components.map((component) => ({
                    placeholder: !!component.placeholder,
                    raw: component,
                    mfg: this.getMfg(component),
                    model: this.getModel(component),
                    flex: component.custom['v-flex'],
                    lengthAdjustment: component.custom['v-shaft-length'],
                    rank: +component.custom['v-rank'],
                    code: component.code,
                }));

                list.sort((a, b) => {
                    if (a.mfg < b.mfg) {
                        return -1;
                    }

                    if (a.mfg > b.mfg) {
                        return 1;
                    }

                    if (a.model < b.model) {
                        return -1;
                    }

                    if (a.model > b.model) {
                        return 1;
                    }

                    return 0;
                });

                return list;
            },

            mfgOptionsForDropdowns() {
                const manufacturers = [...new Set(this.shaftPlacementMapped?.map((component) => component.mfg).filter((x) => x != null))];

                manufacturers.sort();

                return manufacturers.map((x) => ({
                    label: x,
                    code: x,
                }));
            },

            modelOptionsForDropdowns() {
                if (!this.selectedMfgOption) {
                    return []; // No manufacturer selected, return empty options
                }

                const withCurrentManufacturer = this.shaftPlacementMapped
                    .filter((component) => component.mfg === this.selectedMfgOption.code);

                const list = withCurrentManufacturer.reduce((acc, option) => {
                    // Retain codes for each model.
                    const existingOption = acc.find((o) => o.model === option.model);

                    if (existingOption) {
                        existingOption.codes.push(option);
                    } else {
                        const price = this.customizerStore.getOptionPriceValue(this.viewController, option.raw);

                        acc.push({
                            placeholder: option.placeholder,
                            id: option.raw.code,
                            label: option.model,
                            code: option.code,
                            codes: [option],
                            selected: false,
                            mfg: option.mfg,
                            model: option.model,
                            raw: option.raw,
                            image: getImage(this.viewController, option.raw),
                            price: price != null && price.value > 0 ? this.customizerStore.priceFormatted(price.value, true) : '',
                        });
                    }

                    return acc;
                }, []);

                list.sort((a, b) => {
                    if (a.model > b.model) {
                        return 1;
                    }

                    if (a.model < b.model) {
                        return -1;
                    }

                    return 0;
                });

                return list;
            },

            selectedValue() {
                return this.mainPlacement.selectedValue;
            },

            selectedAtPlacements() {
                const visible = [];

                this.itemData.placements.forEach((placement) => {
                    if (placement.custom?.['v-show-selected'] === 'no') {
                        return;
                    }

                    visible.push(placement);
                });

                return visible;
            },

            selectedShaftPlacement() {
                const shaftPlacement = this.selectedAtPlacements.find((placement) => placement.code === 'Shaft');

                const liveSelection = shaftPlacement?.code ? this.viewController?.selectedAtPlacement(shaftPlacement.code) : null;

                return liveSelection;
            },

            isCompleteSelection() {
                return !!this.selectedModel && !!this.selectedMfgOption;
            },

            selectedMfg() {
                return this.selectedMfgOption;
            },

            selectedModel() {
                return this.selectedModelOption;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            visiblePlacements() {
                const placements = this.customizerStore.visiblePlacements(this.itemData) ?? [];
                return placements;
            },
        },

        watch: {
            panelToReset: {
                handler() {
                    if (this.panelToReset === this.currentGroupName) {
                        this.populateFieldsWithItemData();
                    }
                },
                // deep: true,
            },
        },

        mounted() {
            this.populateFieldsWithItemData();
        },

        methods: {
            populateFieldsWithItemData() {
                if (this.itemData) {
                    const selectedManufacturer = this.selectedShaftPlacement.custom['v-manufacturer'];

                    this.selectedMfgOption = this.mfgOptionsForDropdowns.find((option) => option.code === selectedManufacturer);

                    this.selectedModelOption = this.modelOptionsForDropdowns.find((option) => option.model === this.selectedShaftPlacement.custom['v-name']);

                    this.updatePreviewImage();
                }
            },

            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            handleApplySelections() {
                this.setShowGroupOverlay(false);
                this.$emit('applySelections');
            },

            handleMfgSelect(option) {
                this.selectedMfgOption = option;

                this.selectBestMatch(
                    this.viewController,
                    this.mainPlacement,
                    // Any of shafts with the selected manufacturer.
                    this.shaftPlacementMapped.filter((component) => component.mfg === option.label),
                );

                this.populateFieldsWithItemData();
            },

            handleModelSelect(option) {
                this.selectedModelOption = option;

                this.selectBestMatch(
                    this.viewController,
                    this.mainPlacement,
                    // Any of shafts with the selected manufacturer and model.
                    this.shaftPlacementMapped.filter((component) => component.mfg === this.selectedMfgOption.label
                        && component.model === option.model),
                );

                this.populateFieldsWithItemData();
            },

            // eslint-disable-next-line no-unused-vars
            selectBestMatch(controller, placement, candidates) {
                // Find a default shaft.
                let useComponent = candidates.find((x) => !x.raw.placeholder && controller.isAvailable(x.raw)
                    && x.raw.custom?.['v-default-shaft'] === 'true');

                if (useComponent == null) {
                    useComponent = candidates.find((x) => !x.raw.placeholder && controller.isAvailable(x.raw)
                        && x.raw.custom?.['v-length-default'] === 'true');
                }

                if (useComponent == null) {
                    useComponent = candidates.find((x) => !x.raw.placeholder && controller.isAvailable(x.raw));
                }

                if (useComponent) {
                    controller.updateComponent(placement.code, useComponent.raw.code);
                }
            },

            updatePreviewImage() {
                this.previewImage = getImage(this.viewController, this.selectedShaftPlacement);
            },

            getMfg(component) {
                return component?.custom?.['v-manufacturer'];
            },

            getModel(component) {
                return component?.custom?.['v-name'];
            },
        },
    };
</script>
